/**
 * ComplyIQ Module Dashboard Page
 *
 * WHY: This is the landing page for the ComplyIQ module.
 * It provides module-specific metrics and quick access to:
 * - Compliance Management workflows (Controls, Assessments, Evidence)
 * - Audit Management workflows (Audits, Findings)
 * - Mission context within the module
 * - Module-specific KPIs and compliance status
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useMissionContext } from '@/hooks/useMissionContext'
import { api } from '@/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Target,
  ArrowRight,
  Scale,
  FileCheck,
  Gavel,
  Shield,
  ClipboardCheck,
  FileCode,
  BookOpen,
  ShieldCheck,
} from 'lucide-react'

// Workflow cards for ComplyIQ
const workflowCards = [
  {
    id: 'emassist',
    name: 'eMASSist',
    description: 'eMASS-focused authorization support and instance status across configured integrations',
    icon: ShieldCheck,
    href: '/complyiq/emassist',
    available: true,
    items: [
      { name: 'eMASSist Dashboard', href: '/complyiq/emassist', icon: ShieldCheck },
    ],
  },
  {
    id: 'compliance-management',
    name: 'Compliance Management',
    description: 'Track controls, assessments, and evidence for regulatory compliance',
    icon: FileCheck,
    href: '/complyiq/compliance',
    color: 'from-emerald-500/20 to-green-500/20',
    available: true,
    items: [
      { name: 'Controls', href: '/complyiq/compliance/controls', icon: Shield },
      { name: 'Assessments', href: '/complyiq/compliance/assessments', icon: ClipboardCheck },
      { name: 'Evidence', href: '/complyiq/compliance/evidence', icon: FileCode },
    ],
  },
  {
    id: 'audit-management',
    name: 'Audit Management',
    description: 'Manage audits, track findings, and remediation activities',
    icon: Gavel,
    href: '/complyiq/audit',
    color: 'from-amber-500/20 to-yellow-500/20',
    available: true,
    items: [
      { name: 'Audits', href: '/complyiq/audit/audits', icon: BookOpen },
      { name: 'Findings', href: '/complyiq/audit/findings', icon: Shield },
    ],
  },
]

export function ComplyIQDashboardPage() {
  useAuth() // Ensure authenticated
  const { selectedMissionId, setSelectedMissionId } = useMissionContext()

  // Fetch missions
  const { data: missions = [] } = useQuery({
    queryKey: ['platform', 'missions'],
    queryFn: () => api.platform.missions.list(),
  })

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Module Header */}
      <div className="p-2">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <Scale className="h-6 w-6 text-emerald-400" />
              </div>
            </div>
            <h1 className="text-2xl font-display font-bold">
              Compliance Dashboard
            </h1>
            <p className="mt-2 text-muted-foreground max-w-xl">
              Compliance management, audit tracking, and eMASS authorization support.
            </p>
            <div className="mt-6 flex gap-4">
              <Button asChild variant="glow">
                <Link to="/complyiq/compliance">
                  Compliance Management
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/complyiq/audit">
                  Audit Management
                </Link>
              </Button>
            </div>
          </div>

          {/* Mission Context Selector */}
          {missions.length > 0 && (
            <div className="min-w-[250px]">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                Mission Context
              </label>
              <Select
                value={selectedMissionId || undefined}
                onValueChange={setSelectedMissionId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a mission" />
                </SelectTrigger>
                <SelectContent>
                  {missions.map((mission) => (
                    <SelectItem key={mission.mission_id} value={mission.mission_id}>
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        <span>{mission.mission_name}</span>
                        {mission.status === 'active' && (
                          <Badge variant="default" className="text-xs ml-2">
                            Active
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Workflow Cards */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Workflows</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {workflowCards.map((workflow) => {
            const WorkflowIcon = workflow.icon

            return (
              <Card
                key={workflow.id}
                className={`relative overflow-hidden transition-all duration-300 ${
                  workflow.available ? 'hover:border-primary/50' : 'opacity-60'
                }`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="p-2 rounded-lg bg-background/50">
                      <WorkflowIcon className="h-5 w-5" />
                    </div>
                    {!workflow.available && (
                      <Badge variant="secondary" className="text-xs">
                        Coming Soon
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="mt-3 text-base">{workflow.name}</CardTitle>
                  <CardDescription className="text-sm">{workflow.description}</CardDescription>
                </CardHeader>
                {workflow.available && workflow.items.length > 0 && (
                  <CardContent className="pt-0">
                    <div className="space-y-1">
                      {workflow.items.map((item) => {
                        const ItemIcon = item.icon
                        return (
                          <Button
                            key={item.href}
                            asChild
                            variant="ghost"
                            className="w-full justify-start h-9"
                          >
                            <Link to={item.href}>
                              <ItemIcon className="h-4 w-4 mr-2" />
                              {item.name}
                            </Link>
                          </Button>
                        )
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            )
          })}
        </div>
      </div>

    </div>
  )
}
